// Apolo IDE — PlanCreateModal (sprint plan-tab-v1, E2).
//
// Modal simples pra criar um plano. Nome + emoji picker básico (8 sugestões)
// + cor (4 swatches). Sem libs externas — focus trap manual com tabindex.

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Sparkles, X } from 'lucide-react';

const EMOJIS = ['📋', '🚀', '🧠', '🛠', '🔍', '🎯', '✨', '🛡'];
const COLORS = [
  { id: 'violet', value: '#8b5cf6', label: 'Violeta' },
  { id: 'indigo', value: '#6366f1', label: 'Índigo' },
  { id: 'pink',   value: '#ec4899', label: 'Rosa' },
  { id: 'emerald',value: '#10b981', label: 'Esmeralda' },
];

export default function PlanCreateModal({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('📋');
  const [color, setColor] = useState('#8b5cf6');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(''); setEmoji('📋'); setColor('#8b5cf6'); setBusy(false); setErr(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    function esc(e) { if (e.key === 'Escape' && open) onClose(); }
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  async function submit(e) {
    e?.preventDefault?.();
    const n = name.trim();
    if (!n) { setErr('Dê um nome ao plano'); return; }
    setBusy(true);
    setErr(null);
    const res = await onCreate({ name: n, emoji, color });
    setBusy(false);
    if (!res?.ok) setErr(res?.error || 'Falha ao criar plano');
  }

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="plan-create-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 16, scale: 0.96 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-apolo-plan-accentBorder bg-apolo-surface1 text-apolo-textPrimary"
            style={{ boxShadow: 'var(--apolo-plan-glow-wide), 0 20px 60px rgba(0,0,0,0.4)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-2 border-b border-apolo-borderSubtle px-5 py-4">
              <span
                aria-hidden="true"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full"
                style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
              >
                <Sparkles size={16} className="text-white" />
              </span>
              <div className="flex flex-1 flex-col">
                <h2 id="plan-create-title" className="text-base font-semibold tracking-tight">Novo plano</h2>
                <p className="text-xs text-apolo-textTertiary">Espaço pra discovery → arquitetura → entrega.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
              >
                <X size={16} />
              </button>
            </div>

            <form className="flex flex-col gap-4 px-5 py-5" onSubmit={submit}>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-apolo-textSecondary">Nome do plano</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  placeholder="Ex: Lançamento Apolo · v2"
                  className="h-10 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-3 text-sm focus:border-apolo-plan-accentBorder focus:outline-none focus:ring-2 focus:ring-apolo-plan-accentRing"
                />
              </label>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-apolo-textSecondary">Emoji</span>
                <div className="flex flex-wrap gap-1.5">
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setEmoji(e)}
                      aria-pressed={emoji === e}
                      aria-label={`Emoji ${e}`}
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-md border text-lg transition ${
                        emoji === e
                          ? 'border-apolo-plan-accentBorder bg-apolo-plan-accentMuted'
                          : 'border-apolo-borderSubtle bg-apolo-bg hover:border-apolo-borderStrong'
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-apolo-textSecondary">Cor de identidade</span>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setColor(c.value)}
                      aria-pressed={color === c.value}
                      aria-label={c.label}
                      className={`inline-flex h-7 w-7 items-center justify-center rounded-full border-2 transition ${
                        color === c.value ? 'border-apolo-textPrimary' : 'border-transparent'
                      }`}
                      style={{ background: c.value }}
                    >
                      {color === c.value ? <span aria-hidden="true" className="h-2 w-2 rounded-full bg-white" /> : null}
                    </button>
                  ))}
                </div>
              </div>

              {err ? <p className="text-xs text-apolo-error">{err}</p> : null}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 items-center rounded-md px-3 text-sm text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busy || !name.trim()}
                  className="inline-flex h-9 items-center gap-2 rounded-md px-4 text-sm font-semibold text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-plan-accentRing disabled:opacity-60"
                  style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} />}
                  Criar plano
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
